import React, { useEffect, useState, useRef } from 'react';
import { Search, Paperclip, Send, MoreVertical, Phone, Video, Check, CheckCheck, TrendingUp, Lightbulb, MessageSquare, Target, Sparkles, Copy, ThumbsUp } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Avatar } from '../components/ui/Avatar';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { conversations as initialConversations } from '../utils/mockData';
export function WhatsApp() {
  const {
    addToast
  } = useToast();
  const [conversations, setConversations] = useState(initialConversations);
  const [selectedId, setSelectedId] = useState(initialConversations[0].id);
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const selectedConversation = conversations.find(c => c.id === selectedId);
  // Mock AI insights that would come from LLM analysis
  const aiInsights = {
    closingProbability: 78,
    stage: 'Negociação Avançada',
    sentiment: 'Positivo',
    keyInsights: ['Cliente demonstrou interesse em volume maior', 'Mencionou orçamento disponível', 'Perguntou sobre prazo de entrega'],
    suggestedResponses: ['Temos condições especiais para pedidos acima de R$ 5.000. Posso enviar nossa tabela de descontos?', 'Nosso prazo de entrega para São Paulo é de 2-3 dias úteis. Podemos agilizar se necessário.', 'Baseado no seu interesse, preparei uma proposta personalizada. Posso enviar agora?'],
    similarSuccess: {
      customer: 'Glamour & Cia',
      approach: 'Oferta de desconto progressivo + entrega expressa',
      result: 'Fechou em 2 dias - R$ 8.500'
    },
    nextActions: ['Enviar proposta formal', 'Agendar demonstração de produtos', 'Oferecer condições especiais']
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    const newMessage = {
      id: Math.random().toString(),
      text: messageInput,
      sender: 'me' as const,
      timestamp: new Date().toISOString(),
      status: 'sent' as const
    };
    setConversations(prev => prev.map(c => {
      if (c.id === selectedId) {
        return {
          ...c,
          messages: [...c.messages, newMessage],
          lastMessage: messageInput,
          timestamp: new Date().toISOString()
        };
      }
      return c;
    }));
    setMessageInput('');
    // Simulate reply
    setTimeout(() => {
      setConversations(prev => prev.map(c => {
        if (c.id === selectedId) {
          return {
            ...c,
            messages: [...c.messages, {
              id: Math.random().toString(),
              text: 'Obrigado pelo contato! Vou verificar.',
              sender: 'them' as const,
              timestamp: new Date().toISOString(),
              status: 'read' as const
            }]
          };
        }
        return c;
      }));
      addToast('Nova mensagem recebida', 'info');
    }, 2000);
  };
  const handleCopySuggestion = (text: string) => {
    setMessageInput(text);
    addToast('Sugestão copiada para o campo de mensagem', 'success');
  };
  const filteredConversations = conversations.filter(c => c.contactName.toLowerCase().includes(searchTerm.toLowerCase()) || c.storeName.toLowerCase().includes(searchTerm.toLowerCase()));
  return <Layout data-id="element-657">
      <div className="h-[calc(100vh-120px)] bg-card rounded-2xl border border-white/5 overflow-hidden flex" data-id="element-658">
        {/* Sidebar List */}
        <div className="w-full md:w-72 border-r border-slate-700 flex flex-col bg-slate-900/50" data-id="element-659">
          <div className="p-4 border-b border-slate-700" data-id="element-660">
            <h2 className="text-lg font-bold text-white mb-4" data-id="element-661">Conversas</h2>
            <Input placeholder="Buscar conversa..." icon={<Search className="h-4 w-4" data-id="element-663" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-662" />
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-664">
            {filteredConversations.map(conv => <div key={conv.id} onClick={() => setSelectedId(conv.id)} className={`
                  p-4 flex gap-3 cursor-pointer transition-colors border-b border-slate-800/50
                  ${selectedId === conv.id ? 'bg-slate-800' : 'hover:bg-slate-800/50'}
                `} data-id="element-665">
                <Avatar src={conv.avatar} fallback={conv.contactName} data-id="element-666" />
                <div className="flex-1 overflow-hidden" data-id="element-667">
                  <div className="flex justify-between items-baseline mb-1" data-id="element-668">
                    <span className="font-medium text-white truncate" data-id="element-669">
                      {conv.contactName}
                    </span>
                    <span className="text-xs text-text-muted whitespace-nowrap" data-id="element-670">
                      {new Date(conv.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-671">
                    <p className="text-sm text-text-muted truncate pr-2" data-id="element-672">
                      {conv.lastMessage}
                    </p>
                    {conv.unreadCount > 0 && <span className="h-5 w-5 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center flex-shrink-0" data-id="element-673">
                        {conv.unreadCount}
                      </span>}
                  </div>
                  <p className="text-xs text-primary mt-1" data-id="element-674">{conv.storeName}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Chat Area */}
        <div className="hidden md:flex flex-1 flex-col bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat bg-fixed" data-id="element-675">
          {selectedConversation ? <>
              {/* Chat Header */}
              <div className="p-4 bg-card border-b border-slate-700 flex justify-between items-center" data-id="element-676">
                <div className="flex items-center gap-3" data-id="element-677">
                  <Avatar src={selectedConversation.avatar} fallback={selectedConversation.contactName} data-id="element-678" />
                  <div data-id="element-679">
                    <h3 className="font-bold text-white" data-id="element-680">
                      {selectedConversation.contactName}
                    </h3>
                    <p className="text-xs text-text-muted" data-id="element-681">
                      {selectedConversation.storeName}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 text-text-muted" data-id="element-682">
                  <Phone className="h-5 w-5 hover:text-white cursor-pointer" data-id="element-683" />
                  <Video className="h-5 w-5 hover:text-white cursor-pointer" data-id="element-684" />
                  <MoreVertical className="h-5 w-5 hover:text-white cursor-pointer" data-id="element-685" />
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-900/90 backdrop-blur-sm" data-id="element-686">
                {selectedConversation.messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`} data-id="element-687">
                    <div className={`
                      max-w-[70%] rounded-2xl px-4 py-2 shadow-sm relative
                      ${msg.sender === 'me' ? 'bg-primary text-white rounded-tr-none' : 'bg-slate-700 text-white rounded-tl-none'}
                    `} data-id="element-688">
                      <p className="text-sm" data-id="element-689">{msg.text}</p>
                      <div className={`flex items-center justify-end gap-1 mt-1`} data-id="element-690">
                        <span className={`text-[10px] ${msg.sender === 'me' ? 'text-primary-light' : 'text-slate-400'}`} data-id="element-691">
                          {new Date(msg.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                        </span>
                        {msg.sender === 'me' && (msg.status === 'read' ? <CheckCheck className="h-3 w-3 text-blue-300" data-id="element-692" /> : <Check className="h-3 w-3 text-white/70" data-id="element-693" />)}
                      </div>
                    </div>
                  </div>)}
                <div ref={messagesEndRef} data-id="element-694" />
              </div>

              {/* Input Area */}
              <form onSubmit={handleSendMessage} className="p-4 bg-card border-t border-slate-700 flex gap-4 items-center" data-id="element-695">
                <button type="button" className="text-text-muted hover:text-white" data-id="element-696">
                  <Paperclip className="h-5 w-5" data-id="element-697" />
                </button>
                <input type="text" value={messageInput} onChange={e => setMessageInput(e.target.value)} placeholder="Digite uma mensagem..." className="flex-1 bg-slate-800 border-none rounded-full px-4 py-2.5 text-white focus:ring-2 focus:ring-primary placeholder-text-muted" data-id="element-698" />
                <button type="submit" disabled={!messageInput.trim()} className="bg-primary hover:bg-primary-hover text-white p-2.5 rounded-full transition-colors shadow-lg shadow-primary/25 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-699">
                  <Send className="h-5 w-5" data-id="element-700" />
                </button>
              </form>
            </> : <div className="flex-1 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm" data-id="element-701">
              <div className="text-center text-text-muted" data-id="element-702">
                <p data-id="element-703">Selecione uma conversa para começar</p>
              </div>
            </div>}
        </div>

        {/* AI Insights Panel - RIGHT SIDEBAR */}
        <div className="hidden lg:flex w-80 border-l border-slate-700 flex-col bg-slate-900/50 overflow-y-auto custom-scrollbar" data-id="element-704">
          <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-primary/10 to-secondary/10" data-id="element-705">
            <div className="flex items-center gap-2 mb-1" data-id="element-706">
              <Sparkles className="h-5 w-5 text-primary" data-id="element-707" />
              <h3 className="font-bold text-white" data-id="element-708">Assistente IA</h3>
            </div>
            <p className="text-xs text-text-muted" data-id="element-709">
              Análise em tempo real da conversa
            </p>
          </div>

          <div className="p-4 space-y-4" data-id="element-710">
            {/* Closing Probability */}
            <Card className="p-4 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20" data-id="element-711">
              <div className="flex items-center justify-between mb-3" data-id="element-712">
                <div className="flex items-center gap-2" data-id="element-713">
                  <Target className="h-4 w-4 text-emerald-400" data-id="element-714" />
                  <span className="text-sm font-medium text-white" data-id="element-715">
                    Chance de Fechamento
                  </span>
                </div>
                <Badge variant="success" data-id="element-716">{aiInsights.stage}</Badge>
              </div>

              <div className="flex items-center gap-4" data-id="element-717">
                <div className="relative w-20 h-20" data-id="element-718">
                  <svg className="w-20 h-20 transform -rotate-90" data-id="element-719">
                    <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" className="text-slate-700" data-id="element-720" />
                    <circle cx="40" cy="40" r="32" stroke="currentColor" strokeWidth="6" fill="none" strokeDasharray={`${2 * Math.PI * 32}`} strokeDashoffset={`${2 * Math.PI * 32 * (1 - aiInsights.closingProbability / 100)}`} className="text-emerald-400 transition-all duration-1000" strokeLinecap="round" data-id="element-721" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-722">
                    <span className="text-2xl font-bold text-emerald-400" data-id="element-723">
                      {aiInsights.closingProbability}%
                    </span>
                  </div>
                </div>

                <div className="flex-1" data-id="element-724">
                  <div className="flex items-center gap-1 text-emerald-400 text-xs mb-1" data-id="element-725">
                    <TrendingUp className="h-3 w-3" data-id="element-726" />
                    <span data-id="element-727">Alta probabilidade</span>
                  </div>
                  <p className="text-xs text-text-muted" data-id="element-728">
                    Baseado em {aiInsights.keyInsights.length} sinais positivos
                  </p>
                </div>
              </div>
            </Card>

            {/* Key Insights */}
            <div data-id="element-729">
              <div className="flex items-center gap-2 mb-3" data-id="element-730">
                <Lightbulb className="h-4 w-4 text-accent" data-id="element-731" />
                <h4 className="text-sm font-bold text-white" data-id="element-732">
                  Insights da Conversa
                </h4>
              </div>
              <div className="space-y-2" data-id="element-733">
                {aiInsights.keyInsights.map((insight, idx) => <div key={idx} className="flex gap-2 p-2 bg-slate-800/50 rounded-lg border border-slate-700/50" data-id="element-734">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" data-id="element-735" />
                    <p className="text-xs text-text-secondary" data-id="element-736">{insight}</p>
                  </div>)}
              </div>
            </div>

            {/* Suggested Responses */}
            <div data-id="element-737">
              <div className="flex items-center gap-2 mb-3" data-id="element-738">
                <MessageSquare className="h-4 w-4 text-secondary" data-id="element-739" />
                <h4 className="text-sm font-bold text-white" data-id="element-740">
                  Respostas Sugeridas
                </h4>
              </div>
              <div className="space-y-2" data-id="element-741">
                {aiInsights.suggestedResponses.map((response, idx) => <Card key={idx} className="p-3 hover:border-primary/50 transition-all cursor-pointer group" onClick={() => handleCopySuggestion(response)} data-id="element-742">
                    <div className="flex justify-between items-start gap-2 mb-2" data-id="element-743">
                      <p className="text-xs text-text-secondary leading-relaxed flex-1" data-id="element-744">
                        {response}
                      </p>
                      <Copy className="h-3 w-3 text-text-muted group-hover:text-primary flex-shrink-0" data-id="element-745" />
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-text-muted" data-id="element-746">
                      <ThumbsUp className="h-3 w-3" data-id="element-747" />
                      <span data-id="element-748">Efetiva em conversas similares</span>
                    </div>
                  </Card>)}
              </div>
            </div>

            {/* Similar Success Case */}
            <Card className="p-4 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20" data-id="element-749">
              <div className="flex items-center gap-2 mb-3" data-id="element-750">
                <div className="h-6 w-6 rounded-full bg-accent/20 flex items-center justify-center" data-id="element-751">
                  <span className="text-xs" data-id="element-752">💡</span>
                </div>
                <h4 className="text-sm font-bold text-white" data-id="element-753">
                  Caso de Sucesso Similar
                </h4>
              </div>
              <div className="space-y-2" data-id="element-754">
                <div className="flex justify-between text-xs" data-id="element-755">
                  <span className="text-text-muted" data-id="element-756">Cliente:</span>
                  <span className="text-white font-medium" data-id="element-757">
                    {aiInsights.similarSuccess.customer}
                  </span>
                </div>
                <div className="text-xs" data-id="element-758">
                  <span className="text-text-muted" data-id="element-759">Abordagem:</span>
                  <p className="text-text-secondary mt-1" data-id="element-760">
                    {aiInsights.similarSuccess.approach}
                  </p>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-700" data-id="element-761">
                  <span className="text-xs text-text-muted" data-id="element-762">Resultado:</span>
                  <span className="text-xs font-bold text-emerald-400" data-id="element-763">
                    {aiInsights.similarSuccess.result}
                  </span>
                </div>
              </div>
            </Card>

            {/* Next Actions */}
            <div data-id="element-764">
              <div className="flex items-center gap-2 mb-3" data-id="element-765">
                <Target className="h-4 w-4 text-primary" data-id="element-766" />
                <h4 className="text-sm font-bold text-white" data-id="element-767">Próximas Ações</h4>
              </div>
              <div className="space-y-2" data-id="element-768">
                {aiInsights.nextActions.map((action, idx) => <Button key={idx} variant="outline" size="sm" className="w-full justify-start text-xs" data-id="element-769">
                    <span className="mr-2" data-id="element-770">{idx + 1}.</span>
                    {action}
                  </Button>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
}